import React from "react";
import { useForm } from "@tanstack/react-form";
import { toast } from "sonner";
import { Save, Paperclip, Upload, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SaveActionButtons } from "@/components/shared/SaveActionButtons";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ComboboxFK } from "@/components/shared/ComboboxFK";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import {
  TanstackFieldWrapper,
  TanstackFormControl,
} from "@/components/ui/tanstack-form";
import { receiptFormSchema, type ReceiptFormData } from "@/shared/schemas";
import { formatMoney, cn, extractErrorMessage } from "@/lib/utils";
import { NumericInput } from "@/components/shared/NumericInput";
import { CustomerDebtDisplay } from "./CustomerDebtDisplay";
import { uploadFile } from "@/lib/uploadFile";
import { F } from "@/components/shared/F";
import { focusFirstError } from "@/lib/form/formUtils";

import { toVnDateString } from "@apv-erp/status";
interface ReceiptFormProps {
  defaultValues?: Partial<ReceiptFormData>;
  onSubmit: (data: ReceiptFormData) => Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
  submittingLabel?: string;
}

function FieldInfo({ field }: { field: any }) {
  if (!field.state.meta.errors || field.state.meta.errors.length === 0) return null;
  const messages = field.state.meta.errors
    .map((e: any) => (typeof e === "string" ? e : e?.message ? String(e.message) : ""))
    .filter(Boolean);
  if (messages.length === 0) return null;
  return (
    <p className="text-[11px] text-red-500 mt-1">
      {messages.join(", ")}
    </p>
  );
}

const typeOptions = [
  { id: "INCOME", label: "Thu" },
  { id: "EXPENSE", label: "Chi" },
];

const categoryOptions = [
  { id: "THU_TIEN_HANG", label: "Thu tiền hàng" },
  { id: "DAT_COC", label: "Đặt cọc" },
  { id: "HOAN_TIEN", label: "Hoàn tiền" },
  { id: "KHAC", label: "Khác" },
];

const sourceOptions = [
  { id: "ORDER", label: "Từ đơn hàng" },
  { id: "QUOTATION", label: "Từ báo giá" },
  { id: "KHAC", label: "Khác" },
];

const pmOptions = [
  { id: "TIEN_MAT", label: "Tiền mặt" },
  { id: "CHUYEN_KHOAN", label: "Chuyển khoản" },
  { id: "CAN_TRU", label: "Cấn trừ" },
];

const quickTags = [
  "Thanh toán đủ",
  "Đặt cọc đơn hàng",
  "Thanh toán đợt 1",
  "Thanh toán đợt 2",
  "Hoàn tiền",
  "Chi lương tháng",
  "Thanh toán NCC",
];

export default function ReceiptForm({
  defaultValues,
  onSubmit,
  onCancel,
  submitLabel = "Tạo phiếu thu",
  submittingLabel = "Đang tạo...",
}: ReceiptFormProps) {
  // Lưu nháp / Lưu (plan-draft-and-save-buttons-v1)
  const currentStatus = (defaultValues as any)?.receiptId ? ((defaultValues as any)?.status as string | undefined) : undefined;
  const saveModeRef = React.useRef<"DRAFT" | "SUBMIT">("SUBMIT");
  const [isDraftSaving, setIsDraftSaving] = React.useState(false);

  const form = useForm({
    defaultValues: {
      receiptId: defaultValues?.receiptId ?? "",
      type: defaultValues?.type ?? "INCOME",
      categoryId: defaultValues?.categoryId ?? "THU_TIEN_HANG",
      sourceType: defaultValues?.sourceType ?? "ORDER",
      customerId: defaultValues?.customerId ?? "",
      amount: defaultValues?.amount ?? 0,
      receiptDate:
        defaultValues?.receiptDate ?? toVnDateString(),
      paymentMethod: defaultValues?.paymentMethod ?? "CHUYEN_KHOAN",
      referenceNumber: defaultValues?.referenceNumber ?? "",
      notes: defaultValues?.notes ?? "",
      attachments: defaultValues?.attachments ?? [],
      status: defaultValues?.status ?? "CONFIRMED",
    },
    validators: {
      onChange: receiptFormSchema as unknown as undefined,
    },
    onSubmit: async ({ value }) => {
      await onSubmit({ ...(value as ReceiptFormData), saveMode: saveModeRef.current } as any);
    },
  });

  // Lưu nháp: ghi tạm thông tin đang có, không kiểm tra đủ thông tin
  const handleSaveDraft = async () => {
    const values = form.state.values as any;
    if (!values.customerId) {
      toast.error("Chọn khách hàng trước khi lưu nháp");
      return;
    }
    setIsDraftSaving(true);
    try {
      await onSubmit({ ...values, saveMode: "DRAFT" } as any);
    } finally {
      setIsDraftSaving(false);
    }
  };

  const [attachmentLink, setAttachmentLink] = React.useState("");
  const [isUploading, setIsUploading] = React.useState(false);
  

  const handleAddAttachment = (field: any) => {
    if (!attachmentLink.trim()) return;
    field.handleChange([...field.state.value, attachmentLink]);
    setAttachmentLink("");
  };

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    field: any,
  ) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    const newUrls: string[] = [];

    for (const file of Array.from(files)) {
      try {
        const result = await uploadFile(file);
        newUrls.push(result.url);
      } catch (err: any) {
        toast.error(`Lỗi upload ${file.name}: ${extractErrorMessage(err)}`);
      }
    }

    if (newUrls.length > 0) {
      field.handleChange([...(field.state.value || []), ...newUrls]);
    }

    setIsUploading(false);
    e.target.value = ""; // reset input
  };

  return (
    <div className="space-y-4 w-full mx-auto pb-8">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          saveModeRef.current = "SUBMIT";
          form.handleSubmit();
          requestAnimationFrame(() => focusFirstError(form.state as any));
        }}
        className="space-y-4"
      >
        <div className="bg-slate-50/50 rounded-lg p-4 border border-slate-100">
          <h3 className="font-semibold text-slate-800 border-b pb-2 mb-3 text-sm">
            Phân loại
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <form.Field
              name="type"
              children={(field) => (
                <div className="space-y-1.5" id="type" data-field="type">
                  <label className="block text-xs font-semibold text-slate-700">
                    Loại phiếu <span className="text-red-500">*</span>
                  </label>
                  <ComboboxFK
                    id="type"
                    dataField="type"
                    required={true}
                    value={field.state.value}
                    onChange={(v) =>
                      field.handleChange(
                        (v || "INCOME") as "INCOME" | "EXPENSE",
                      )
                    }
                    options={typeOptions}
                    className="h-9"
                  />
                  <FieldInfo field={field} />
                </div>
              )}
            />
            <form.Field
              name="categoryId"
              children={(field) => (
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-slate-700">
                    Hạng mục
                  </label>
                  <ComboboxFK
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v || "")}
                    options={categoryOptions}
                    placeholder="Chọn hạng mục..."
                    className="h-9"
                  />
                  <FieldInfo field={field} />
                </div>
              )}
            />
            <form.Field
              name="sourceType"
              children={(field) => (
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-slate-700">
                    Từ quy trình
                  </label>
                  <ComboboxFK
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v || "")}
                    options={sourceOptions}
                    placeholder="Chọn quy trình..."
                    className="h-9"
                  />
                  <FieldInfo field={field} />
                </div>
              )}
            />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200">
          <div className="px-4 py-3 border-b bg-slate-50/50">
            <h3 className="font-semibold text-slate-800 text-sm">
              Thông tin thu
            </h3>
          </div>
          <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <form.Field
              name="customerId"
              children={(field) => (
                <div
                  className="col-span-1 md:col-span-2 space-y-1.5"
                  id="customerId"
                  data-field="customerId"
                >
                  <label className="block text-xs font-semibold text-slate-700">
                    Đối tượng (Khách hàng){" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <SharedCombobox
                    id="customerId"
                    dataField="customerId"
                    required={true}
                    error={Boolean(field.state.meta.errors?.length)}
                    type="customer"
                    value={field.state.value}
                    valueLabel={(defaultValues as any)?.customer?.shortName || (defaultValues as any)?.customer?.name}
                    onChange={(v) => field.handleChange(v || "")}
                    placeholder="Chọn khách hàng..."
                  />
                  <FieldInfo field={field} />
                </div>
              )}
            />

            <form.Field
              name="amount"
              children={(field) => (
                <div
                  className="col-span-1 space-y-1.5"
                  id="amount"
                  data-field="amount"
                >
                  <label className="block text-xs font-semibold text-slate-700">
                    Số tiền <span className="text-red-500">*</span>
                  </label>
                  <NumericInput
                    id="amount"
                    name="amount"
                    value={field.state.value}
                    onChange={(val) => field.handleChange(val || 0)}
                    className={cn(
                      "h-9 text-base font-bold",
                      field.state.meta.errors?.length ? "border-red-500 ring-1 ring-red-500" : "",
                    )}
                    placeholder="0"
                  />
                  <FieldInfo field={field} />
                </div>
              )}
            />
            <div className="col-span-1" id="receiptDate" data-field="receiptDate">
              <F
                form={form}
                name="receiptDate"
                label="Ngày thu"
                type="date"
                required
              />
            </div>

            <form.Field
              name="paymentMethod"
              children={(field) => (
                <div
                  className="col-span-1 space-y-1.5"
                  id="paymentMethod"
                  data-field="paymentMethod"
                >
                  <label className="block text-xs font-semibold text-slate-700">
                    Hình thức <span className="text-red-500">*</span>
                  </label>
                  <ComboboxFK
                    id="paymentMethod"
                    dataField="paymentMethod"
                    required={true}
                    value={field.state.value}
                    onChange={(v) =>
                      field.handleChange(
                        (v || "CHUYEN_KHOAN") as
                          "TIEN_MAT" | "CHUYEN_KHOAN" | "CAN_TRU",
                      )
                    }
                    options={pmOptions}
                    className="h-9"
                  />
                  <FieldInfo field={field} />
                </div>
              )}
            />
            <div className="col-span-1">
              <F
                form={form}
                name="referenceNumber"
                label="Số tham chiếu"
                placeholder="Số GD ngân hàng..."
              />
            </div>

            <form.Field
              name="notes"
              children={(field) => (
                <div className="col-span-1 md:col-span-2 space-y-1.5">
                  <label className="block text-xs font-semibold text-slate-700">
                    Ghi chú / Lý do
                  </label>
                  <Textarea
                    value={field.state.value || ""}
                    onChange={(e) => field.handleChange(e.target.value)}
                    rows={2}
                    placeholder="Nhập lý do..."
                    className="resize-none"
                  />
                  <div className="flex flex-wrap gap-2 mt-2">
                    {quickTags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() =>
                          field.handleChange(
                            (field.state.value
                              ? field.state.value + ", "
                              : "") + tag,
                          )
                        }
                        className="px-2.5 py-1 text-[11px] bg-slate-100 text-slate-600 rounded-full hover:bg-slate-200 transition-colors border border-slate-200"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                  <FieldInfo field={field} />
                </div>
              )}
            />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <form.Field
            name="attachments"
            children={(field) => (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Paperclip className="w-4 h-4 text-slate-500" />
                  <h3 className="font-semibold text-slate-800 text-sm">
                    Chứng từ đính kèm
                  </h3>
                </div>
                <div className="flex gap-2">
                  <label className="flex items-center justify-center px-4 py-2 bg-white border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer transition-colors shadow-sm gap-2 h-9">
                    {isUploading ? (
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-700" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    {isUploading ? "Đang tải..." : "Upload File"}
                    <input
                      type="file"
                      className="hidden"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                      onChange={(e) => handleFileUpload(e, field)}
                      disabled={isUploading}
                    />
                  </label>
                  <Input
                    placeholder="Hoặc dán URL vào đây..."
                    className="flex-1 text-sm h-9"
                    value={attachmentLink}
                    onChange={(e) => setAttachmentLink(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddAttachment(field);
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="h-9"
                    onClick={() => handleAddAttachment(field)}
                  >
                    Thêm URL
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {[
                    "Hóa đơn",
                    "Phiếu thu",
                    "Ủy nhiệm chi",
                    "Biên nhận",
                    "Hợp đồng",
                  ].map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => {
                        field.handleChange([...field.state.value, tag]);
                      }}
                      className="px-2 py-1 text-[11px] bg-slate-50 text-slate-500 rounded hover:bg-slate-100 border transition-colors flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" /> {tag}
                    </button>
                  ))}
                </div>
                {field.state.value && field.state.value.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
                    {field.state.value.map((att: any, idx: number) => {
                      const isString = typeof att === "string";
                      const url = isString ? att : att.url || "#";
                      const name = isString
                        ? att.split("/").pop() || "Đính kèm"
                        : att.name || "Đính kèm";
                      return (
                        <div
                          key={idx}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 text-xs rounded-md border border-blue-100"
                        >
                          <Paperclip className="w-3 h-3" />
                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="max-w-[200px] truncate hover:underline"
                          >
                            {name}
                          </a>
                          <button
                            type="button"
                            className="ml-1 hover:text-blue-900"
                            onClick={() => {
                              const newValue = [...field.state.value];
                              newValue.splice(idx, 1);
                              field.handleChange(newValue);
                            }}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          />
        </div>

        <form.Subscribe
          selector={(state) => state.values.customerId}
          children={(customerId) => (
            <CustomerDebtDisplay customerId={customerId} />
          )}
        />

        <div className="sticky bottom-0 z-50 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 p-4 sm:p-6 bg-white/95 backdrop-blur-md border-t border-slate-200 mt-6 flex justify-start gap-3 shadow-[0_-4px_6px_-1px_rgb(0,0,0,0.05)]">
          <form.Subscribe
            selector={(state) => [state.isSubmitting]}
            children={([isSubmitting]) => (
              <SaveActionButtons
                status={currentStatus}
                busy={Boolean(isSubmitting || isDraftSaving)}
                onSaveDraft={handleSaveDraft}
                submitLabel={submitLabel}
              />
            )}
          />
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="min-w-[100px]"
          >
            Huỷ
          </Button>
        </div>
      </form>
    </div>
  );
}
